/** @format */

import PropTypes from "prop-types";
import { useEffect } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// material
import { styled } from "@mui/material/styles";
import { Avatar, Box, Drawer, Link, Typography } from "@mui/material";
// hooks
import useResponsive from "../../hooks/useResponsive";
// components
import Logo from "../../components/Logo";
import Scrollbar from "../../components/Scrollbar";
import NavSection from "../../components/NavSection";
//
import sidebarConfig from "../../configs/SidebarConfig";
import Constants from "../../configs/Constants";
import User from "../../dao/User";
import Swal from "../../reusables/CustomSwal";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const AccountStyle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: theme.palette.grey[500_12],
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  let email = useSelector(({ auth }) => auth?.auth?.email);
  let user = useSelector(({ auth }) => auth?.auth);
  let status = useSelector(({ waba }) => waba?.waba);
  
  
  // console.log("SC", sidebarConfig(user?.role));

  const isDesktop = useResponsive("up", "lg");

  const check2FAStatus = async () => {
    try {
      const data = await User.getTOTPStatus();
      if (data.success) {
        
        // If 2FA is not enabled and we haven't shown the prompt yet and not on profile page
        if (!data.totpEnabled  && !pathname.includes('/profile')) {
          
          const result = await Swal.fire({
            title: 'Secure Your Account',
            text: 'For enhanced security, we recommend setting up two-factor authentication.',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Setup Now',
            cancelButtonText: 'Maybe Later',
            confirmButtonColor: '#1976d2',
          });
          
          if (result.isConfirmed) {
            navigate('/profile');
          }
        }
      }
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      // Don't show error for 2FA status check failure
    }
  };

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Check 2FA status when user is authenticated and component mounts
  useEffect(() => {
    if (user?.token) {
      check2FAStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* <Typography variant="h4" sx={{ color: "white" }}>
          Infomedia Panel
        </Typography> */}
        <Logo />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline="none" component={RouterLink} to="/profile">
          <AccountStyle>
            <Avatar src={Constants.defaultPhotoURL} alt="photoURL" />
            <Box sx={{ ml: 2, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ color: "white" }}>
                {user?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "white" }}>
                {user?.role === "SUPER_ADMIN"
                  ? "Super Admin"
                  : user?.role === "ADMIN"
                  ? "Admin"
                  : "Member"}
              </Typography>
            </Box>
          </AccountStyle>
        </Link>
      </Box>

      <NavSection
        navConfig={sidebarConfig(user?.role, status?.status, email)}
      />
      {/*<NavSection navConfig={sidebarConfig(role)} />*/}

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle>
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: "background.default",
              borderRightStyle: "dashed",
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
